import { ColumnDef } from '@tanstack/react-table'
import { TagCell } from 'components/table/cells/TagCell'
import { TextCell } from 'components/table/cells/TextCell'
import {
  ThumbnailCell,
  ThumbnailLinkCell,
} from 'components/table/cells/ThumbnailCell'
import { Thumbnail } from 'components/table/utility/Thumbnail'
import { AssetImage } from 'features/assetDatabase/components/assetImage/AssetImage'
import { AssetTableAssetViz } from 'features/assetDatabase/components/AssetTableAssetViz'
import { AssetData } from 'features/assetDatabase/types/assetTableTypes'
import React, { useMemo } from 'react'
import { InputType } from 'types'
import { compareAlphanumeric } from 'utils/stringHelpers'

import { convertDateToString } from './AssetTableHelpers'
import { InspectionTypesCell } from './cells/InspectionTypesCell'

export enum AssetTableColumn {
  Name = 'name',
  LastInspectionDate = 'lastInspectionDate',
  LastUpdated = 'lastUpdated',
  UnitComponents = 'unitComponents',
  InspectionTypes = 'inspectionTypes',
  Area = 'area',
  AreaDescription = 'areaDescription',
  ErpId = 'erpId',
  Files = 'files',
}

const AssetTableColumns = (
  siteID: string
): {
  defaultColumns: ColumnDef<AssetData>[]
  hiddenColumns: ColumnDef<AssetData>[]
} => {
  return useMemo(() => {
    const defaultColumns: ColumnDef<AssetData>[] = [
      {
        accessorKey: AssetTableColumn.Name,
        id: AssetTableColumn.Name,
        header: 'Asset',
        filterFn: 'includesString',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => {
          return (
            <ThumbnailCell
              data-testid={`asset-${row.original.id}`}
              thumbnailString={row.getValue(AssetTableColumn.Name)}>
              <Thumbnail>
                <AssetImage
                  primaryImageID={row.original.unitPrimaryImage}
                  assetModelType={row.original.assetModelType}
                />
              </Thumbnail>
            </ThumbnailCell>
          )
        },
      },
      {
        accessorKey: AssetTableColumn.LastInspectionDate,
        id: AssetTableColumn.LastInspectionDate,
        header: 'Last Inspection',
        sortingFn: 'datetime',
        sortUndefined: 'last',
        filterFn: 'dateRangeFilter',
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => (
          <ThumbnailLinkCell
            thumbnailString={convertDateToString(
              row.getValue(AssetTableColumn.LastInspectionDate)
            )}
            to={
              convertDateToString(row.original.lastInspectionDate)
                ? '/details/$entryID'
                : '/assets/$siteID/$unitID'
            }
            from='/assets/{-$siteID}'
            params={{
              entryID: row.original.lastInspectionSlug,
              siteID,
              unitID: row.original.id,
            }}
            search={{ view: 'details' }}
            hoverable={Boolean(row.original.lastInspectionDate)}
            emptyText={'Not yet inspected'}>
            {
              <Thumbnail>
                <AssetTableAssetViz
                  cardProps={row.original.cardProps}
                  unitID={row.original.id}
                  siteID={siteID}
                />
              </Thumbnail>
            }
          </ThumbnailLinkCell>
        ),
      },
      {
        accessorKey: AssetTableColumn.LastUpdated,
        id: AssetTableColumn.LastUpdated,
        header: 'Last Updated',
        sortingFn: 'datetime',
        sortUndefined: 'last',
        filterFn: 'dateRangeFilter',
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => (
          <TextCell
            text={convertDateToString(
              row.getValue(AssetTableColumn.LastUpdated)
            )}
          />
        ),
      },
      {
        accessorKey: AssetTableColumn.UnitComponents,
        id: AssetTableColumn.UnitComponents,
        header: 'Components',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        meta: {
          inputType: InputType.Number,
        },
        cell: ({ row }) => (
          <TagCell
            text={(
              row.getValue(AssetTableColumn.UnitComponents) as number
            ).toString()}
          />
        ),
      },
      {
        accessorKey: AssetTableColumn.InspectionTypes,
        id: AssetTableColumn.InspectionTypes,
        header: 'Data Layers',
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.getValue(
            AssetTableColumn.InspectionTypes
          ) as string[]
          const valueB = rowB.getValue(
            AssetTableColumn.InspectionTypes
          ) as string[]
          return valueA.length - valueB.length
        },
        sortUndefined: 'last',
        filterFn: 'multiSelectFilter',
        meta: {
          inputType: InputType.MultiSelect,
        },
        cell: ({ row }) => (
          <InspectionTypesCell
            inspectionTypes={row.getValue(AssetTableColumn.InspectionTypes)}
          />
        ),
      },
      {
        accessorKey: AssetTableColumn.Area,
        id: AssetTableColumn.Area,
        header: 'Area',
        sortingFn: (rowA, rowB) => {
          const valueA =
            rowA.original.area === undefined ? '' : rowA.original.area
          const valueB =
            rowB.original.area === undefined ? '' : rowB.original.area
          return compareAlphanumeric(valueA, valueB)
        },
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Combobox,
        },
        cell: ({ row }) => (
          <TextCell
            text={row.getValue(AssetTableColumn.Area)}
            subtitle={row.original.areaDescription ?? ''}
          />
        ),
      },
      {
        accessorKey: AssetTableColumn.AreaDescription,
        id: AssetTableColumn.AreaDescription,
        header: 'Area Description',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.getValue(AssetTableColumn.AreaDescription)} />
        ),
      },
      {
        accessorKey: AssetTableColumn.ErpId,
        id: AssetTableColumn.ErpId,
        header: 'ERP ID',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.getValue(AssetTableColumn.ErpId)} />
        ),
      },
    ]
    const hiddenColumns: ColumnDef<AssetData>[] = [
      {
        accessorKey: AssetTableColumn.Files,
        id: AssetTableColumn.Files,
        header: 'Files',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        meta: {
          inputType: InputType.Number,
        },
        cell: ({ row }) => (
          <TextCell
            text={(row.getValue(AssetTableColumn.Files) as number).toString()}
          />
        ),
      },
    ]

    return { defaultColumns, hiddenColumns }
  }, [siteID])
}

export default AssetTableColumns
